import { useState, useCallback } from 'react';
import type { Area, Point } from 'react-easy-crop';
import { PRINT_RATIOS, type PrintRatio } from '@/types';
import { ZOOM_CONFIG } from '@/constants/cropper';
import type { CropState } from '@/utils/cropTransformers';

function createInitialCropStates(): Record<PrintRatio, CropState> {
  return Object.fromEntries(
    PRINT_RATIOS.map(ratio => [
      ratio,
      { crop: { x: 0, y: 0 }, zoom: ZOOM_CONFIG.DEFAULT, croppedAreaPixels: null }
    ])
  ) as Record<PrintRatio, CropState>;
}

export function useCropState() {
  const [cropStates, setCropStates] = useState<Record<PrintRatio, CropState>>(createInitialCropStates);

  const onCropChange = useCallback((ratio: PrintRatio, crop: Point) => {
    setCropStates(prev => ({
      ...prev,
      [ratio]: { ...prev[ratio], crop },
    }));
  }, []);

  const onZoomChange = useCallback((ratio: PrintRatio, zoom: number) => {
    setCropStates(prev => ({
      ...prev,
      [ratio]: { ...prev[ratio], zoom },
    }));
  }, []);

  const onCropComplete = useCallback((ratio: PrintRatio, _croppedArea: Area, croppedAreaPixels: Area) => {
    setCropStates(prev => ({
      ...prev,
      [ratio]: { ...prev[ratio], croppedAreaPixels },
    }));
  }, []);

  const resetCrop = useCallback((ratio: PrintRatio) => {
    setCropStates(prev => ({
      ...prev,
      [ratio]: {
        crop: { x: 0, y: 0 },
        zoom: ZOOM_CONFIG.DEFAULT,
        croppedAreaPixels: null
      },
    }));
  }, []);

  const resetAllCrops = useCallback(() => {
    setCropStates(createInitialCropStates());
  }, []);

  return {
    cropStates,
    onCropChange,
    onZoomChange,
    onCropComplete,
    resetCrop,
    resetAllCrops,
  };
}

